import { createPortal } from "react-dom";
import deLocale from "@fullcalendar/core/locales/de";
import dayGridPlugin from "@fullcalendar/daygrid";
import type { CustomButtonInput, ToolbarInput } from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import type { Theme } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useMemo, useRef, useState } from "react";
import { useGetList, useGetManyReference } from "react-admin";

import { AddAppointmentDialog } from "../../dialogs/AddAppointmentDialog/AddAppointmentDialog";
import { EventContent } from "../../misc/calendar/CalendarEventCardContent";
import { AutovioCalendarEvent, isPracticalExam } from "../../model/autovioCalendarEvents";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import {
  autovioCalendarEventToEventInput,
  calendarSlotLabelFormat,
  eventClassNames,
  publicHolidayToEventInput,
} from "../../utils/calendar";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { useDateFromHashOrNow } from "../../hooks/useDateFromHashOrNow";
import { useDialog } from "../../hooks/useDialog";
import { grants } from "../../backoffice.access_control";
import { useRefetchCalendarEventsCounter } from "../../hooks/useRefetchCalendarEventsCounter";
import { DateRange } from "../../model/DateRange";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { useRecoilState } from "recoil";
import { persistentCalendarViewStateFamily } from "../../persistent_state";
import { CalendarDayHeaderContent } from "../../misc/calendar/CalendarDayHeaderContent";
import { Branch } from "../../providers/branchesProvider";
import { Instructor } from "../../providers/instructorsProvider";
import { Row } from "../../misc/Row";
import { useMenu } from "../../hooks/useMenu";
import { useFullcalendarCustomButtonAnchor } from "../../hooks/useFullcalendarCustomButtonAnchor";
import { useDrivingSchoolExamsCalendarWarnings } from "../../hooks/useDrivingSchoolExamsCalendarWarnings";

const examTypeChoices = [
  { id: "all", name: "Alle Prüfungen" },
  { id: "TheoryExam", name: "Theoretische Prüfungen" },
  { id: "PracticalExam", name: "Praktische Prüfungen" },
] as const;

type ExamTypeFilter = (typeof examTypeChoices)[number]["id"];
type ExamsCalendarView = "timeGridDay" | "timeGridWeek" | "dayGridMonth";

const calendarViewChoices = [
  { id: "timeGridDay", name: "Tag" },
  { id: "timeGridWeek", name: "Woche" },
  { id: "dayGridMonth", name: "Monat" },
] as const satisfies Array<{ id: ExamsCalendarView; name: string }>;

export function DrivingSchoolExamsCalendar() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const calendarRef = useRef<FullCalendar>(null);
  const [{ drivingSchoolId }] = useAutovioContext();
  const initialDate = useDateFromHashOrNow();
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedExamType, setSelectedExamType] = useState<ExamTypeFilter>("all");
  const selectedExamTypes = selectedExamType === "all" ? ["TheoryExam", "PracticalExam"] : [selectedExamType];
  const [calendarTitle, setCalendarTitle] = useState("");
  const branchDropdownAnchor = useFullcalendarCustomButtonAnchor("branchDropdownAnchor");
  const threeDotsMenuAnchor = useFullcalendarCustomButtonAnchor("threeDotsMenuAnchor");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>([
    DateWithoutTime.fromDateTime(initialDate.startOf("week")),
    DateWithoutTime.fromDateTime(initialDate.endOf("week")),
  ]);
  const [persistedView, setPersistedView] = useRecoilState(
    persistentCalendarViewStateFamily("DrivingSchoolExamsCalendar"),
  );
  const pdfDateRange = useMemo(() => getPdfDateRange(dateRange, persistedView), [dateRange, persistedView]);
  const shouldFetchPdfDateRange = !dateRangesEqual(dateRange, pdfDateRange);
  const refetchCounter = useRefetchCalendarEventsCounter();
  const { data, isLoading } = useGetManyReference<AutovioCalendarEvent>(
    "calendarEvents",
    {
      target: "drivingSchoolId",
      id: drivingSchoolId,
      filter: {
        dateRange,
        type: selectedExamTypes,
      },
      pagination: { page: 1, perPage: 9999 },
      meta: { refetchCounter },
    },
    {
      enabled: !!drivingSchoolId,
    },
  );
  const { data: pdfData, isLoading: isLoadingPdfData } = useGetManyReference<AutovioCalendarEvent>(
    "calendarEvents",
    {
      target: "drivingSchoolId",
      id: drivingSchoolId,
      filter: {
        dateRange: pdfDateRange,
        type: selectedExamTypes,
      },
      pagination: { page: 1, perPage: 9999 },
      meta: { refetchCounter },
    },
    {
      enabled: !!drivingSchoolId && shouldFetchPdfDateRange,
    },
  );
  const { data: activeBranches } = useGetList<Branch>(
    "branches",
    {
      filter: { drivingSchoolId: drivingSchoolId, isActive: true },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const { data: instructors = [] } = useGetList<Instructor>(
    "instructors",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );

  const instructorsBranchesMap = useMemo(() => {
    const map: Record<string, Array<string>> = {};
    for (const instructor of instructors) {
      map[instructor.id] = instructor.assignedBranches ?? [];
    }
    return map;
  }, [instructors]);

  const filterEventsBySelectedBranch = useCallback(
    (events: Array<AutovioCalendarEvent> | undefined) => {
      if (!events || !selectedBranchId) {
        return events;
      }
      return events.filter((event) => {
        if (isPracticalExam(event)) {
          return !!event.instructorId && !!instructorsBranchesMap[event.instructorId]?.includes(selectedBranchId);
        }
        return event.branchId === selectedBranchId;
      });
    },
    [selectedBranchId, instructorsBranchesMap],
  );
  const filteredData = useMemo(() => filterEventsBySelectedBranch(data), [filterEventsBySelectedBranch, data]);
  const filteredPdfData = useMemo(() => filterEventsBySelectedBranch(pdfData), [filterEventsBySelectedBranch, pdfData]);
  const { warningsByDay, warningsByEventId } = useDrivingSchoolExamsCalendarWarnings(filteredData);

  const publicHolidays = useMemo(() => {
    const knownPublicHolidays = new Set<number>();
    return (
      selectedBranchId ? activeBranches?.filter((branch) => branch.id === selectedBranchId) : (activeBranches ?? [])
    )
      ?.flatMap((branch) => branch.publicHolidays ?? [])
      .reduce(
        (acc, publicHoliday) => {
          const { date } = publicHoliday;
          if (knownPublicHolidays.has(date)) {
            return acc;
          }
          knownPublicHolidays.add(date);
          if (date < dateRange[0].valueOf() || date > dateRange[1].valueOf()) {
            return acc;
          }
          return [...acc, { ...publicHoliday, date: DateWithoutTime.fromNumber(date) }];
        },
        [] as Array<{ name: string; date: DateWithoutTime }>,
      );
  }, [activeBranches, selectedBranchId, dateRange]);
  const minHour = filteredData?.reduce((acc, it) => Math.min(acc, it.start.hour), 6) ?? 6;
  const { dialogProps, openDialog } = useDialog();
  const canCreateExam = grants.has("Exam:create");
  const events = useMemo(
    () => [
      ...(filteredData?.map((event) =>
        autovioCalendarEventToEventInput(event, { warnings: warningsByEventId.get(event.id) }),
      ) ?? []),
      ...(publicHolidays || []).map(publicHolidayToEventInput),
    ],
    [filteredData, warningsByEventId, publicHolidays],
  );
  const openExamsCalendarPdf = useCallback(async () => {
    const pdfEvents = shouldFetchPdfDateRange ? filteredPdfData : filteredData;
    if (!pdfEvents || !drivingSchoolId) {
      return;
    }
    setIsGeneratingPdf(true);
    const pdfWindow = window.open("", "_blank");
    await runSafely(
      async () => {
        const { generateExamsCalendarPdf } = await import("./examsCalendarPdf");
        const pdf = await generateExamsCalendarPdf(
          pdfEvents,
          pdfDateRange[0].toDateTime(),
          pdfDateRange[1].toDateTime().endOf("day"),
          drivingSchoolId,
        );
        const pdfBytes = new Uint8Array(pdf);
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const pdfObjectUrl = URL.createObjectURL(blob);
        if (pdfWindow) {
          pdfWindow.location.href = pdfObjectUrl;
        } else {
          window.open(pdfObjectUrl, "_blank");
        }
        window.setTimeout(() => URL.revokeObjectURL(pdfObjectUrl), 60_000);
      },
      { errorMessage: "Fehler beim Erstellen des Prüfungskalenders.", successMessage: "" },
    );
    setIsGeneratingPdf(false);
  }, [filteredData, filteredPdfData, pdfDateRange, drivingSchoolId, shouldFetchPdfDateRange]);
  const changeCalendarView = useCallback((view: ExamsCalendarView) => {
    calendarRef.current?.getApi().changeView(view);
  }, []);
  const headerToolbar = useMemo<ToolbarInput>(
    () => ({
      left: "branchDropdownAnchor",
      center: "",
      right: `prev today next ${canCreateExam && !isMobile ? "addAppointmentButton " : ""}timeGridDay,timeGridWeek,dayGridMonth threeDotsMenuAnchor`,
    }),
    [canCreateExam, isMobile],
  );
  const customButtons = useMemo<{ [name: string]: CustomButtonInput }>(
    () => ({
      addAppointmentButton: { text: "＋\u00A0Termin", click: openDialog, hint: "Neuen Termin hinzufügen" },
      branchDropdownAnchor: {},
      threeDotsMenuAnchor: {},
    }),
    [openDialog],
  );

  if (!drivingSchoolId || !activeBranches) {
    return null;
  }

  return (
    <Box data-testid="DrivingSchoolExamsCalendar" className="exams-calendar">
      <div className="exams-calendar-card-header">
        <h2 className="fc-toolbar-title exams-calendar-title">{calendarTitle}</h2>
        <div className="exams-calendar-mobile-header-menu">
          {canCreateExam && <ExamsCalendarCreateMenuButton openDialog={openDialog} />}
          <ExamsCalendarHeaderMenuButton
            isDisabled={isGeneratingPdf || isLoadingPdfData}
            label={isGeneratingPdf ? "PDF-Prüfungskalender wird erstellt ..." : "PDF-Prüfungskalender erstellen"}
            activeCalendarView={persistedView ?? "timeGridWeek"}
            changeCalendarView={changeCalendarView}
            openExamsCalendarPdf={openExamsCalendarPdf}
          />
        </div>
      </div>
      <FullCalendar
        ref={calendarRef}
        timeZone="Europe/Berlin"
        locales={[deLocale]}
        height="auto"
        slotMinTime={`0${minHour}:00:00`}
        slotLabelFormat={calendarSlotLabelFormat}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        eventMinHeight={1}
        headerToolbar={headerToolbar}
        customButtons={customButtons}
        initialDate={initialDate.toISO()}
        initialView={persistedView ?? "timeGridWeek"}
        scrollTime="08:00:00"
        allDaySlot={false}
        editable={false}
        weekNumbers={true}
        showNonCurrentDates={false}
        dayMaxEvents={true}
        nowIndicator={true}
        events={events}
        eventClassNames={eventClassNames}
        eventContent={(eventInfo) => <EventContent eventInfo={eventInfo} mode="DrivingSchoolExamsCalendar" />}
        dayHeaderContent={(arg) => <CalendarDayHeaderContent arg={arg} warningsByDay={warningsByDay} />}
        datesSet={(dates) => {
          setCalendarTitle(dates.view.title);
          if (dates.view?.type && persistedView !== dates.view.type) {
            setPersistedView(dates.view.type);
          }
          const start = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.start, { zone: "Europe/Berlin" }));
          const end = DateWithoutTime.fromDateTime(DateTime.fromJSDate(dates.end, { zone: "Europe/Berlin" })).prevDay;
          if (dateRange[0].toString() !== start.toString() || dateRange[1].toString() !== end.toString()) {
            setDateRange([start, end]);
          }
        }}
        dateClick={canCreateExam ? AddAppointmentDialog.fullCalendarDateClickHandler(openDialog) : undefined}
      />
      <AddAppointmentDialog {...dialogProps} tabs={["exam"]} />
      {isLoading && <LoadingIndicator />}
      {branchDropdownAnchor &&
        (createPortal(
          <Row className="fc-exams-calendar-filters">
            {activeBranches.length > 1 && (
              <Row className="exams-calendar-branch-filter" sx={{ position: "relative" }}>
                <Tooltip title="Beim Filtern nach Filiale wird die Zuordnung der Fahrlehrer zu einer Filiale berücksichtigt.">
                  <IconButton
                    size="small"
                    aria-label="Hinweis zum Filialfilter"
                    sx={{
                      color: "text.secondary",
                      position: "absolute",
                      top: 2,
                      right: 27,
                      zIndex: 1,
                    }}
                  >
                    <InfoIcon sx={{ fontSize: "22px" }} />
                  </IconButton>
                </Tooltip>
                <FormControl size="small" className="filter-field">
                  <InputLabel id="branch-select-label" shrink>
                    Filiale
                  </InputLabel>
                  <Select
                    labelId="branch-select-label"
                    id="branch-select"
                    value={selectedBranchId}
                    label="Filiale"
                    displayEmpty
                    renderValue={(value) =>
                      value ? activeBranches.find((branch) => branch.id === value)?.name : "Alle Filialen"
                    }
                    onChange={(event) => setSelectedBranchId(event.target.value)}
                  >
                    <MenuItem value="">Alle Filialen</MenuItem>
                    {activeBranches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Row>
            )}
            <FormControl size="small" className="filter-field">
              <InputLabel id="exam-type-select-label">Prüfungsart</InputLabel>
              <Select
                labelId="exam-type-select-label"
                id="exam-type-select"
                value={selectedExamType}
                label="Prüfungsart"
                onChange={(event) => setSelectedExamType(event.target.value as ExamTypeFilter)}
              >
                {examTypeChoices.map((choice) => (
                  <MenuItem key={choice.id} value={choice.id}>
                    {choice.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Row>,
          branchDropdownAnchor,
        ) as any)}
      {threeDotsMenuAnchor &&
        (createPortal(
          <ExamsCalendarHeaderMenuButton
            isDisabled={isGeneratingPdf || isLoadingPdfData}
            label={isGeneratingPdf ? "PDF-Prüfungskalender wird erstellt ..." : "PDF-Prüfungskalender erstellen"}
            openExamsCalendarPdf={openExamsCalendarPdf}
          />,
          threeDotsMenuAnchor,
        ) as any)}
    </Box>
  );
}

function ExamsCalendarCreateMenuButton(props: { openDialog: () => void }) {
  const { openDialog } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <div>
      <IconButton size="small" onClick={openMenu} aria-label="Prüfungstermin hinzufügen">
        <AddIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            openDialog();
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          Termin
        </MenuItem>
      </Menu>
    </div>
  );
}

function ExamsCalendarHeaderMenuButton(props: {
  activeCalendarView?: string;
  changeCalendarView?: (view: ExamsCalendarView) => void;
  isDisabled: boolean;
  label: string;
  openExamsCalendarPdf: () => Promise<void>;
}) {
  const { activeCalendarView, changeCalendarView, isDisabled, label, openExamsCalendarPdf } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <div>
      <IconButton
        size="small"
        onClick={openMenu}
        disabled={isDisabled && !changeCalendarView}
        aria-label="Prüfungskalender-Menü öffnen"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        <MenuItem
          disabled={isDisabled}
          onClick={() => {
            closeMenu();
            void openExamsCalendarPdf();
          }}
        >
          {label}
        </MenuItem>
        {changeCalendarView && [
          <ListSubheader key="view-subheader">Ansicht</ListSubheader>,
          ...calendarViewChoices.map((choice) => (
            <MenuItem
              key={choice.id}
              selected={activeCalendarView === choice.id}
              onClick={() => {
                closeMenu();
                changeCalendarView(choice.id);
              }}
            >
              {choice.name}
            </MenuItem>
          )),
        ]}
      </Menu>
    </div>
  );
}

function getPdfDateRange(dateRange: DateRange, view: string | undefined): DateRange {
  if (view !== "timeGridDay") {
    return dateRange;
  }
  const selectedDate = dateRange[0].toDateTime();
  return [
    DateWithoutTime.fromDateTime(selectedDate.startOf("week")),
    DateWithoutTime.fromDateTime(selectedDate.endOf("week")),
  ];
}

function dateRangesEqual(a: DateRange, b: DateRange): boolean {
  return a[0].toString() === b[0].toString() && a[1].toString() === b[1].toString();
}
